import React, { useState, useEffect } from 'react';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import { useWebRTC } from '../../context/WebRTCContext';
import './Topnav.MessageQueue.css';

/**
 * MessageQueueIndicator - Shows pending messages waiting for DataChannel
 *
 * Design: A subtle outbox-style indicator that pulses when messages are queued.
 * Uses warm amber to indicate "pending" state, matching the app's accent palette.
 * Only visible when there are queued messages.
 */
const TopnavMessageQueue = () => {
  const { messageQueue, messageQueueLength, flushMessageQueue, connectedPeers } = useWebRTC();
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate time since oldest message
  const oldestMessage = messageQueue[0];
  const [waitTime, setWaitTime] = useState(0);

  useEffect(() => {
    if (!oldestMessage) {
      setWaitTime(0);
      return;
    }

    const updateWaitTime = () => {
      const elapsed = Math.floor((Date.now() - oldestMessage.queuedAt) / 1000);
      setWaitTime(elapsed);
    };

    updateWaitTime();
    const interval = setInterval(updateWaitTime, 1000);
    return () => clearInterval(interval);
  }, [oldestMessage]);

  // Don't render if no queued messages
  if (messageQueueLength === 0) {
    return null;
  }

  const hasDataChannel = connectedPeers.length > 0;

  // Format wait time
  const formatWaitTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Categorize messages by type
  const messageTypes = messageQueue.reduce((acc, msg) => {
    const type = msg.feature ? `${msg.feature}.${msg.cmd}` : msg.type;
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="message-queue-indicator">
      <UncontrolledDropdown>
        <DropdownToggle
          className="queue-toggle"
          color="empty"
          data-tooltip={`${messageQueueLength} command${messageQueueLength !== 1 ? 's' : ''} waiting to reach the device`}
        >
          <div className="queue-icon-wrapper">
            <svg
              className="queue-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {/* Outbox/send icon */}
              <path d="M22 2L11 13" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" />
            </svg>
            <span className="queue-count">{messageQueueLength}</span>
            <div className="pulse-ring" />
          </div>
        </DropdownToggle>

        <DropdownMenu className="queue-dropdown" end>
          <div className="queue-header">
            <div className="queue-title">
              <svg className="queue-title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>Waiting to send</span>
            </div>
            <div className="queue-wait-time">
              Waiting {formatWaitTime(waitTime)}
            </div>
          </div>

          <div className="queue-status">
            {hasDataChannel ? (
              <div className="status-connected">
                <span className="status-dot connected" />
                <span>Device connected</span>
              </div>
            ) : (
              <div className="status-disconnected">
                <span className="status-dot disconnected" />
                <span>Device not connected</span>
              </div>
            )}
          </div>

          <div className="queue-messages">
            {Object.entries(messageTypes).map(([type, count]) => (
              <div key={type} className="queue-message-type">
                <span className="message-type-name">{type}</span>
                <span className="message-type-count">×{count}</span>
              </div>
            ))}
          </div>

          {hasDataChannel && (
            <button
              className="queue-flush-btn"
              onClick={(e) => {
                e.preventDefault();
                flushMessageQueue();
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              Send Now
            </button>
          )}

          <div className="queue-footer">
            Commands wait here until the device reconnects
          </div>
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

export default TopnavMessageQueue;
